import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BOOKS, AI_BOOKS, BUY_LINKS, COUNTRIES } from "../data/content";
import useCountry from "../hooks/useCountry";
import CountryPicker from "../components/site/CountryPicker";
import BuyDropdown from "../components/site/BuyDropdown";

const SERIES_GROUPS = [
  {
    id: "finance",
    label: "Finance for Little Hands",
    books: BOOKS,
    number: "01",
    formats: "Paperback + Kindle eBooks",
  },
  {
    id: "ai",
    label: "AI for Little Hands",
    books: AI_BOOKS,
    number: "02",
    formats: "Paperback",
  },
];

function BuyCTA({ book, country }) {
  const url = country ? BUY_LINKS[book.id]?.[country] : null;
  const store =
    COUNTRIES.find((c) => c.code === country)?.store || "Amazon";

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-buy !py-2.5 !px-4 !text-[11px] whitespace-nowrap"
        data-testid={`socials-buy-${book.id}`}
      >
        Buy on {store}
        <span>↗</span>
      </a>
    );
  }
  // No detected country — inline the flip-up dropdown
  return <BuyDropdown book={book} />;
}

function BookRow({ book, country }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="py-4 border-t border-stone-900/12 first:border-t-0"
      data-testid={`socials-book-${book.id}`}
    >
      <div className="flex items-start gap-4">
        <img
          src={book.cover}
          alt={book.title}
          loading="lazy"
          className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-[3px] border border-stone-900/10 shadow-[0_6px_18px_rgba(28,25,23,0.12)] shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-[0.28em] uppercase text-stone-500 leading-none">
            Book № {book.number} · Series № {book.seriesNumber}
          </p>
          <p className="mt-1.5 font-serif text-[17px] md:text-[19px] leading-[1.15]">
            {book.title}
          </p>
          <p className="mt-1 text-[12px] text-stone-500 leading-snug">
            {book.subtitle}
          </p>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <BuyCTA book={book} country={country} />
      </div>
    </motion.li>
  );
}

export default function SocialsPage() {
  const { country, meta, status, setCountry, clearCountry } = useCountry();

  return (
    <div
      className="min-h-screen relative"
      style={{ background: "var(--paper)" }}
      data-testid="socials-page"
    >
      <div className="grain-overlay" aria-hidden="true" />

      <div className="mx-auto max-w-[480px] px-5 pt-10 pb-16">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-[10px] mx-auto relative"
               style={{ background: "var(--ink)" }}>
            <span className="font-serif italic font-bold text-2xl leading-none text-[color:var(--paper)]">B</span>
            <span
              className="absolute rounded-full"
              style={{
                width: "6px",
                height: "6px",
                top: "6px",
                right: "8px",
                background: "var(--accent-orange)",
              }}
            />
          </div>
          <h1 className="mt-4 font-serif italic text-3xl md:text-4xl leading-tight">
            Books for Little Hands
          </h1>
          <p className="mt-1 text-[11px] uppercase tracking-[0.3em] text-stone-500">
            Illustrated picture books · Ages 4 — 10
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-stone-700">
            Story on one side, hands-on activities on the other. Building early financial &amp; AI literacy in curious little minds.
          </p>
        </motion.header>

        {/* Country pill */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-6 flex flex-col items-center gap-2 text-[11px] tracking-[0.22em] uppercase"
          data-testid="socials-country-bar"
        >
          <span className="text-stone-500">
            {status === "loading" && "Detecting your country…"}
            {(status === "detected" || status === "manual" || status === "default") && meta && (
              <>Shopping from</>
            )}
            {status === "unknown" && "Choose your country to shop:"}
          </span>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <CountryPicker country={country} onChange={setCountry} />
            {country && (
              <button
                type="button"
                onClick={clearCountry}
                className="text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-400"
                data-testid="socials-clear-country"
              >
                reset
              </button>
            )}
          </div>
        </motion.div>

        {/* Shop the Series */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="mt-10"
          data-testid="socials-shop"
        >
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="folio-num">Shop the Series</h2>
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone-500">
              06 Books · 02 Series
            </span>
          </div>

          {SERIES_GROUPS.map((group) => (
            <div
              key={group.id}
              className="mb-6 border border-stone-900/12 bg-[color:var(--paper)] p-4 md:p-5"
              data-testid={`socials-series-${group.id}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <p className="font-serif italic text-xl md:text-2xl leading-tight">
                    {group.label}
                  </p>
                  <p
                    className="mt-1 text-[9px] uppercase tracking-[0.22em] text-stone-500"
                    data-testid={`socials-series-format-${group.id}`}
                  >
                    {group.formats}
                  </p>
                </div>
                <span className="folio-num text-[9px] shrink-0">Series № {group.number}</span>
              </div>
              <ul>
                {group.books.map((b) => (
                  <BookRow
                    key={b.id}
                    book={{ ...b, seriesNumber: group.number }}
                    country={country}
                  />
                ))}
              </ul>
            </div>
          ))}
        </motion.section>

        {/* Link buttons */}
        <motion.nav
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="mt-8 flex flex-col gap-3"
          data-testid="socials-links"
        >
          <Link
            to="/about"
            className="group flex items-center justify-between px-5 py-4 border border-stone-900/25 hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-300"
            data-testid="socials-link-about"
          >
            <span>
              <span className="block folio-num opacity-70">Story</span>
              <span className="block font-serif text-xl leading-tight mt-0.5">
                About us / our story
              </span>
            </span>
            <span aria-hidden>→</span>
          </Link>

          <Link
            to="/gift-guide"
            className="group flex items-center justify-between px-5 py-4 border border-stone-900/25 bg-[color:var(--paper-warm)] hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-300"
            data-testid="socials-link-gift"
          >
            <span>
              <span className="block folio-num opacity-70">Seasonal</span>
              <span className="block font-serif text-xl leading-tight mt-0.5">
                Gift guide · Ages 4 — 10
              </span>
            </span>
            <span aria-hidden>→</span>
          </Link>

          <a
            href="https://www.instagram.com/books.for.little.hands/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between px-5 py-4 border border-stone-900/25 hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-300"
            data-testid="socials-link-instagram"
          >
            <span>
              <span className="block folio-num opacity-70">Follow</span>
              <span className="block font-serif text-xl leading-tight mt-0.5">
                @books.for.little.hands
              </span>
            </span>
            <span aria-hidden>↗</span>
          </a>

          <Link
            to="/"
            className="group flex items-center justify-between px-5 py-4 border border-stone-900/25 hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-300"
            data-testid="socials-link-home"
          >
            <span>
              <span className="block folio-num opacity-70">Homepage</span>
              <span className="block font-serif text-xl leading-tight mt-0.5">
                booksforlittlehands.com
              </span>
            </span>
            <span aria-hidden>→</span>
          </Link>
        </motion.nav>

        {/* Footer */}
        <p className="mt-10 text-center text-[10px] tracking-[0.3em] uppercase text-stone-500">
          © {new Date().getFullYear()} · Books for Little Hands
        </p>
      </div>
    </div>
  );
}
